// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import Flux2Home from './components/Flux2Image/Flux2Home';

// Components
import Navbar from './components/Navbar';
import Home from './components/Home';
import Community from './components/Community';
import Technology from './components/Technology';
// import Security from './components/Security';
import AdditionalServices from './components/AdditionalServices';
import Chat from './components/Chat';
import Footer from './components/Footer';
import Chatting from './Chatting';
import NicknameSetup from './NicknameSetup';
import LoadingSpinner from './components/LoadingSpinner';
import Dapp from './components/Dapp';
import NFTManage from './components/NFTManage';
import Utils from './components/Utils';
import AdminMenu from './components/AdminMenu';
import ChatboardPage from './components/ChatboardPage';
import NewsboardPage from './components/NewsboardPage';

import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isNicknameSetupOpen, setIsNicknameSetupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 사용자 인증 상태 감지
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        await loadUserProfile(user.uid);
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 사용자 프로필 로드
  const loadUserProfile = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        setUserProfile(userDoc.data());
      } else {
        setUserProfile(null);
      }
    } catch (error) {
      console.error('프로필 로드 오류:', error);
      setUserProfile(null);
    }
  };

  // 모바일 디바이스 감지 함수
  const isMobileDevice = () => {
    // User Agent 기반 감지와 화면 크기 기반 감지를 모두 사용
    const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const mobileScreenSize = window.innerWidth <= 768;
    return mobileUserAgent || mobileScreenSize;
  };

  // PWA 환경 감지 함수
  const isPWAMode = () => {
    // PWA가 standalone 모드로 실행되고 있는지 확인
    return window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true || // iOS Safari PWA
      document.referrer.includes('android-app://'); // Android PWA
  };

  // 디바이스에 따른 윈도우 기능 설정
  const getWindowFeatures = () => {
    if (isMobileDevice()) {
      const screenWidth = window.screen.width;
      let screenHeight = window.screen.height;

      // PWA 모드에서는 높이를 더 높게 설정 (상태바, 주소바 등 고려)
      if (isPWAMode()) {
        screenHeight = window.screen.height + 100; // 100px 여유 추가
        return `width=${screenWidth},height=${screenHeight},fullscreen=yes,resizable=no,scrollbars=no,status=no,menubar=no,toolbar=no,location=no,directories=no,titlebar=no`;

      } else {
        screenHeight = window.screen.height;
        return `width=${screenWidth},height=${screenHeight},fullscreen=yes,resizable=no,scrollbars=no,status=no,menubar=no,toolbar=no,location=no,directories=no,titlebar=no`;
      }

      // 모바일에서는 전체 화면 (window.screen 사용)
      // return `width=${screenWidth},height=${screenHeight},fullscreen=yes,resizable=no,scrollbars=no,status=no,menubar=no,toolbar=no,location=no,directories=no,titlebar=no`;
    } else {
      // 데스크톱에서는 고정 크기
      return 'width=375,height=667,addressbar=no,resizable=no,scrollbars=no,status=no,menubar=no,toolbar=no,location=no,directories=no,titlebar=no,chrome=no';
    }
  };

  // 닉네임 설정
  const handleNicknameSet = async (nickname) => {
    if (!user) return;

    try {
      // MariaDB에서 사용자 정보 업데이트는 이미 NicknameSetup에서 처리됨
      // 여기서는 상태만 업데이트
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        nickname: nickname
      };

      setUserProfile(userData);
      setIsNicknameSetupOpen(false);

      // 닉네임 설정 후 채팅 새 창 열기
      openChatWindow();
    } catch (error) {
      console.error('닉네임 설정 오류:', error);
      throw error;
    }
  };

  // 채팅 새 창 열기 함수 - 모바일 반응형 적용 
  const openChatWindow = () => {
    if (!user || !userProfile?.nickname) {
      console.warn('사용자 정보가 없거나 닉네임이 설정되지 않음');
      return;
    }

    // 디바이스에 따른 새 창 옵션 설정
    const windowFeatures = getWindowFeatures();

    console.log('Opening chat window with features:', windowFeatures);
    console.log('Is mobile device:', isMobileDevice());
    console.log('Is PWA mode:', isPWAMode());

    // 채팅 전용 URL로 새 창 열기
    const chatWindow = window.open('/chat-window', 'WorldLandChat', windowFeatures);

    if (chatWindow) {
      // 새 창에서 준비 완료 메시지를 기다림
      const handleMessage = (event) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'CHAT_WINDOW_READY') {
          // 새 창이 준비되면 사용자 정보 전달
          chatWindow.postMessage({
            type: 'INIT_CHAT',
            user: {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL
            },
            userProfile: userProfile
          }, window.location.origin);

          // 리스너 제거
          window.removeEventListener('message', handleMessage);
        }
      };

      window.addEventListener('message', handleMessage);

      // 30초 후 리스너 정리
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
      }, 30000);
    } else {
      alert('팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <div className="app">
        <Routes>
          {/* Utils 페이지 - Navbar와 Footer 없이 렌더링 */}


          <Route
            path="/chatboard"
            element={
              <ChatboardPage
                user={user}
                userProfile={userProfile}
              />
            }
          />
          <Route
            path="/newsboard"
            element={
              <NewsboardPage
                user={user}
                userProfile={userProfile}
              />
            }
          />

          <Route path="/additional-services" element={
            <AdditionalServices
              user={user}
              userProfile={userProfile}
              setIsNicknameSetupOpen={setIsNicknameSetupOpen}
            />
          } />
          <Route path="/dapp" element={<Dapp user={user} userProfile={userProfile} />} />

          <Route path="/chat" element={
            <Chat
              user={user}
              userProfile={userProfile}
              setIsNicknameSetupOpen={setIsNicknameSetupOpen}
              openChatWindow={openChatWindow}
            />
          } />
          <Route path="/chat-window" element={
            <Chatting
              isNewWindow={true}
              user={user}
              userProfile={userProfile}
            />
          } />
          <Route path="/nft-manage" element={<NFTManage />} />
          <Route path="/myai/flux2image" element={<Flux2Home user={user} userProfile={userProfile} />} />

          <Route path="/utils" element={<Utils user={user} userProfile={userProfile} />} />
          <Route path="/admin-menu" element={<AdminMenu user={user} userProfile={userProfile} />} />

          {/* 나머지 페이지들 - Navbar와 Footer 포함 */}
          <Route path="*" element={
            <>
              <Navbar
                user={user}
                userProfile={userProfile}
                setUser={setUser}
                setUserProfile={setUserProfile}
                setIsNicknameSetupOpen={setIsNicknameSetupOpen}
                openChatWindow={openChatWindow}
              />

              <main className="main-content">
                <Routes>
                  <Route path="/community" element={<Community />} />
                  <Route path="/technology" element={
                    <Technology
                      user={user}
                      userProfile={userProfile}
                    />
                  } />


                  <Route path="/" element={
                    <Home
                      user={user}
                      userProfile={userProfile}
                      setIsNicknameSetupOpen={setIsNicknameSetupOpen}
                      openChatWindow={openChatWindow}
                    />
                  } />

                </Routes>
              </main>

              <Footer />
            </>
          } />
        </Routes>

        {/* Nickname Setup Popup */}
        <NicknameSetup
          user={user}
          onNicknameSet={handleNicknameSet}
          isOpen={isNicknameSetupOpen}
          onClose={() => setIsNicknameSetupOpen(false)}
        />
      </div>
    </Router>
  );
}

export default App;